import http from '@/utils/request';
import type {
  CommandListParams,
  CommandListResult,
  CommandRecord,
  CreateCommandPayload,
  UploadedFileMeta,
} from './command';

/**
 * 门户（微信登录）专用指令接口。
 *
 * 所有接口路径前缀都是 `/portal/command/*`，与后台管理用的 `/command/*` 物理隔离：
 *   - 后端通过 PortalCommandController 强制按当前登录人的 openid 过滤；
 *   - 前端这里不传也不能传"看谁的数据"参数，避免再出现 mineOnly 这种容易越权的开关。
 */

/** 列表 -> 仅返回"我"提交的（后端按 portalOpenid → t_account.id 强制过滤 submitter） */
export const fetchMyCommandList = (params: CommandListParams = {}) =>
  http.get<unknown, CommandListResult>('/portal/command/list', { params });

/**
 * 上传文件到 COS（仅上传，不落库）
 *
 * 支持一次上传多个文件：单文件直传，多文件由服务端 archiver 打包为一个 zip。
 * 字段名固定为 `files`（与后端 FilesInterceptor 对齐）。
 *
 * ⚠️ 超时覆盖：
 *   axios 全局默认 timeout=30s，针对最大 50MB 文件 + 弱网/VPN 场景明显不够
 *   （30s 内传不完就会报 `timeout of 30000ms exceeded`，且因为是客户端 abort，
 *   服务端日志里不会出现任何错误记录）。这里单独放大到 10 分钟。
 *
 * 请求体大小上限由"前端 UI 校验 + Nginx `client_max_body_size` + Nest Multer limits"
 * 三道闸共同把守，axios 层不再重复设置，避免数值不一致带来的维护负担。
 */
export const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000; // 10 分钟

export const uploadMyCommandFiles = (
  files: File[],
  onProgress?: (percent: number) => void,
) => {
  const form = new FormData();
  files.forEach((f) => form.append('files', f));
  return http.post<unknown, UploadedFileMeta>('/portal/command/upload-file', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: UPLOAD_TIMEOUT_MS,
    onUploadProgress: (e) => {
      if (!onProgress || !e.total) return;
      onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
};

/** 新增指令（携带已上传文件的 fileKey 等元数据） */
export const createMyCommand = (payload: CreateCommandPayload) =>
  http.post<unknown, CommandRecord>('/portal/command/create', payload);

/** 获取下载 URL（用不可预测的 fileKey 寻址，配合后端 ownership 校验防越权） */
export const fetchMyCommandDownloadUrl = (fileKey: string) =>
  http.get<unknown, { url: string; fileName: string }>('/portal/command/download-url', {
    params: { fileKey },
  });

/**
 * 获取「完成回执附件」下载 URL
 *
 * 入参字段名仍是 fileKey（复用同一份 DTO），语义上传入的是 CommandRecord.completeFileKey。
 * 后端会按 t_command.complete_file_key 命中记录后校验 ownership，再签发 COS 临时 URL。
 */
export const fetchMyCommandCompleteDownloadUrl = (completeFileKey: string) =>
  http.get<unknown, { url: string; fileName: string }>('/portal/command/complete-download-url', {
    params: { fileKey: completeFileKey },
  });
